<template>
  <div class="relative">
    <!-- Mobile: Regular Grid (No Carousel) -->
    <div class="md:hidden">
      <div class="grid grid-cols-1 gap-6 items-start">
        <slot name="mobile-view" :items="items"></slot>
      </div>
    </div>

    <!-- Desktop/Tablet: Carousel -->
    <div class="hidden md:block">
      <!-- Carousel Container -->
      <div 
        class="overflow-hidden cursor-grab active:cursor-grabbing" 
        ref="carouselContainer"
        @mousedown="handleDragStart"
        @mousemove="handleDragMove"
        @mouseup="handleDragEnd"
        @mouseleave="handleDragEnd"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div 
          class="flex transition-transform duration-500 ease-in-out"
          :class="{ 'transition-none': isDragging }"
          :style="{ transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))` }"
        >
          <!-- Slides -->
          <div 
            v-for="(slide, index) in slides" 
            :key="index"
            class="w-full flex-shrink-0"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start px-2">
              <slot :name="`slide-${index}`" :items="slide"></slot>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <template v-if="slides.length > 1">
      <!-- Previous Button -->
      <button
        @click="previousSlide"
        :disabled="currentIndex === 0"
        :class="[
          'absolute left-0 top-[45%] -translate-y-1/2 -translate-x-4 z-10',
          'bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg',
          'border-2 border-gray-200 dark:border-gray-600',
          'transition-all duration-200',
          currentIndex === 0
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl hover:scale-110 cursor-pointer'
        ]"
        aria-label="Previous slide"
      >
        <ChevronLeft class="h-6 w-6 text-gray-700 dark:text-gray-300" />
      </button>

      <!-- Next Button -->
      <button
        @click="nextSlide"
        :disabled="currentIndex === slides.length - 1"
        :class="[
          'absolute right-0 top-[45%] -translate-y-1/2 translate-x-4 z-10',
          'bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg',
          'border-2 border-gray-200 dark:border-gray-600',
          'transition-all duration-200',
          currentIndex === slides.length - 1
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl hover:scale-110 cursor-pointer'
        ]"
        aria-label="Next slide"
      >
        <ChevronRight class="h-6 w-6 text-gray-700 dark:text-gray-300" />
      </button>
    </template>

    <!-- Pagination Dots -->
    <div v-if="slides.length > 1" class="flex justify-center items-center mt-6 space-x-2">
      <button
        v-for="(slide, index) in slides"
        :key="`dot-${index}`"
        @click="goToSlide(index)"
        :class="[
          'transition-all duration-200',
          currentIndex === index
            ? 'bg-blue-600 dark:bg-blue-500 w-8 h-2 rounded-full'
            : 'bg-gray-300 dark:bg-gray-600 w-2 h-2 rounded-full hover:bg-gray-400 dark:hover:bg-gray-500'
        ]"
        :aria-label="`Go to slide ${index + 1}`"
      ></button>
    </div>

      <!-- Slide Counter -->
      <div v-if="slides.length > 1" class="text-center mt-3">
        <span class="text-sm text-secondary-text dark:text-secondary-dark-text">
          {{ currentIndex + 1 }} / {{ slides.length }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

interface Props {
  items: any[]
  itemsPerSlide?: number
}

const props = withDefaults(defineProps<Props>(), {
  itemsPerSlide: 3
})

// State
const currentIndex = ref(0)
const carouselContainer = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const dragOffset = ref(0)
const startX = ref(0)
const currentX = ref(0)

// Computed
const slides = computed(() => {
  const slideGroups: any[][] = []
  for (let i = 0; i < props.items.length; i += props.itemsPerSlide) {
    slideGroups.push(props.items.slice(i, i + props.itemsPerSlide))
  }
  return slideGroups
})

// Methods
const nextSlide = () => {
  if (currentIndex.value < slides.value.length - 1) {
    currentIndex.value++
  }
}

const previousSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const goToSlide = (index: number) => {
  currentIndex.value = index
}

// Mouse drag handlers
const handleDragStart = (event: MouseEvent) => {
  isDragging.value = true
  startX.value = event.pageX
  currentX.value = event.pageX
  
  // Prevent default drag behavior on images and links
  event.preventDefault()
}

const handleDragMove = (event: MouseEvent) => {
  if (!isDragging.value) return
  
  event.preventDefault()
  currentX.value = event.pageX
  dragOffset.value = currentX.value - startX.value
}

const handleDragEnd = () => {
  if (!isDragging.value) return
  
  const threshold = 50 // Minimum drag distance to trigger slide change
  
  if (dragOffset.value > threshold) {
    // Dragged right - go to previous slide
    previousSlide()
  } else if (dragOffset.value < -threshold) {
    // Dragged left - go to next slide
    nextSlide()
  }
  
  isDragging.value = false
  dragOffset.value = 0
}

// Touch handlers
const handleTouchStart = (event: TouchEvent) => {
  startX.value = event.touches[0].pageX
  currentX.value = event.touches[0].pageX
  isDragging.value = true
}

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging.value) return
  
  currentX.value = event.touches[0].pageX
  dragOffset.value = currentX.value - startX.value
}

const handleTouchEnd = () => {
  if (!isDragging.value) return
  
  const threshold = 50 // Minimum swipe distance to trigger slide change
  
  if (dragOffset.value > threshold) {
    // Swiped right - go to previous slide
    previousSlide()
  } else if (dragOffset.value < -threshold) {
    // Swiped left - go to next slide
    nextSlide()
  }
  
  isDragging.value = false
  dragOffset.value = 0
}

// Watch for items changes and reset to first slide
watch(() => props.items, () => {
  currentIndex.value = 0
})

// Optional: Add keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    previousSlide()
  } else if (event.key === 'ArrowRight') {
    nextSlide()
  }
}

// Add keyboard listeners when component is mounted
import { onMounted, onUnmounted } from 'vue'

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Prevent text selection while dragging */
.cursor-grab:active,
.cursor-grabbing {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>
